/**
 * A task's due time and its overdue mark (wave COM item 1), the same on every task list:
 * the Tasks register, the inbox, Home and a record's own tasks.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The due time is printed in the tenant's zone with the zone's name beside it
 * (lib/zonedTime). The overdue mark is the scheduler's `overdue_at`: the client never
 * decides a task is overdue from the clock, so a task past its due time that the
 * scheduler has not reached yet reads as due, not overdue.
 */

import { isTaskOverdue, type Task } from "../api/tasks";
import { strings } from "../brand/strings";
import { zonedStamp } from "../lib/zonedTime";

export function TaskDue({
  task,
  showNone = false,
}: {
  readonly task: Pick<Task, "dueAt" | "overdueAt" | "completedAt">;
  /** Say "No due time" for a kind without a clock (the register's column does). */
  readonly showNone?: boolean;
}): React.JSX.Element | null {
  const copy = strings.tasks;
  if (task.dueAt === null) {
    return showNone ? (
      <span className="q-note" data-testid="task-due-none">
        {copy.noDue}
      </span>
    ) : null;
  }
  const overdue = isTaskOverdue(task);
  return (
    <span
      className="inline-flex flex-wrap items-center gap-x-2 gap-y-1"
      data-testid="task-due"
      data-overdue={overdue ? "true" : "false"}
    >
      {overdue && (
        <span
          className="q-chip q-chip--danger"
          data-testid="task-overdue"
          title={copy.overdueSince(zonedStamp(task.overdueAt))}
        >
          {copy.overdue}
        </span>
      )}
      <time dateTime={task.dueAt} className="text-sm">
        {copy.dueOn(zonedStamp(task.dueAt))}
      </time>
    </span>
  );
}
